import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import axios from 'axios';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {Cancel, CheckCircle, CloudUpload, VerifiedUser} from '@mui/icons-material';
import {DateTime} from 'luxon';
import {API_ENDPOINT} from '../../config/settings';

const SHOW_FILE_INTEGRITY_CHECK = false;

const TYPE_LABELS: Record<string, {pt: string; en: string}> = {
  delay: {pt: 'Declaração de Atraso', en: 'Delay Declaration'},
  cancellation: {pt: 'Declaração de Cancelamento', en: 'Cancellation Declaration'},
  boarding: {pt: 'Declaração de Embarque', en: 'Boarding Declaration'},
  ticket_usage: {pt: 'Declaração de Utilização de Bilhete', en: 'Ticket Usage Declaration'},
};

const COUPON_STATUS_LABELS: Record<string, {pt: string; en: string}> = {
  O: {pt: 'Aberto', en: 'Open'},
  U: {pt: 'Voado', en: 'Flown'},
  E: {pt: 'Reemitido', en: 'Reissued'},
  R: {pt: 'Reembolsado', en: 'Refunded'},
  X: {pt: 'Cancelado', en: 'Cancelled'},
  A: {pt: 'Expirado', en: 'Expired'},
};

const COUPON_STATUS_COLORS: Record<string, string> = {
  O: '#757575',
  U: '#2e7d32',
  E: '#e65100',
  R: '#c62828',
  X: '#757575',
  A: '#757575',
};

interface VerifyResult {
  valid: boolean;
  message?: string;
  ref?: string;
  type?: string;
  issuedAt?: string;
  details?: Record<string, any>;
}

interface VerifyFormValues {
  reference: string;
  ticketNumber: string;
}

const verifyFormSchema = yup.object().shape({
  reference: yup.string().trim().required('Referência obrigatória'),
  ticketNumber: yup.string().trim().required('Número de bilhete obrigatório'),
});

// Section caption: PT on top (bold, uppercase), EN below (italic, smaller, blue)
const SectionCaption = ({pt, en}: {pt: string; en: string}) => (
  <Box sx={{mb: 0.5}}>
    <Typography
      variant='caption'
      color='text.secondary'
      sx={{fontWeight: 700, letterSpacing: '0.08em', display: 'block', lineHeight: 1.4}}
    >
      {pt}
    </Typography>
    <Typography
      variant='caption'
      sx={{fontStyle: 'italic', color: '#0082BB', letterSpacing: '0.04em', display: 'block', lineHeight: 1.3}}
    >
      {en}
    </Typography>
  </Box>
);

const DetailRow = ({labelPT, labelEN, value}: {labelPT: string; labelEN: string; value: string}) => (
  <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
    <Box sx={{minWidth: 120, flexShrink: 0}}>
      <Typography variant='body2' color='text.secondary' sx={{lineHeight: 1.3}}>
        {labelPT}
      </Typography>
      <Typography variant='caption' sx={{fontStyle: 'italic', color: '#0082BB', display: 'block', lineHeight: 1.2}}>
        {labelEN}
      </Typography>
    </Box>
    <Typography variant='body2' fontWeight='500' sx={{pt: '2px'}}>{value}</Typography>
  </Box>
);

const couponDate = (raw: any): string => {
  const str = typeof raw === 'string' ? raw : raw?.dateTime;
  if (!str) return '';
  const dt = DateTime.fromISO(str);
  return dt.isValid ? dt.toFormat('dd/MM') : String(str);
};

const th = {
  padding: '4px 3px',
  fontWeight: 700,
  fontSize: '0.62rem',
  lineHeight: 1.2,
  color: '#002857',
  bgcolor: '#D6EEF6',
  whiteSpace: 'normal' as const,
  wordBreak: 'break-word' as const,
  textAlign: 'center' as const,
};

const td = {padding: '4px 3px', fontSize: '0.7rem', whiteSpace: 'normal' as const, wordBreak: 'break-word' as const};

const CouponsTable = ({coupons}: {coupons: any[]}) => (
  <TableContainer sx={{border: '1px solid #D6EEF6', borderRadius: 1}}>
    <Table size='small' sx={{borderCollapse: 'collapse', tableLayout: 'fixed', width: '100%'}}>
      <colgroup>
        <col style={{width: '5%'}} />
        <col style={{width: '10%'}} />
        <col style={{width: '11%'}} />
        <col style={{width: '9%'}} />
        <col style={{width: '22%'}} />
        <col style={{width: '10%'}} />
        <col style={{width: '11%'}} />
        <col style={{width: '9%'}} />
        <col style={{width: '13%'}} />
      </colgroup>
      <TableHead>
        <TableRow>
          <TableCell colSpan={4} sx={{...th, bgcolor: '#002857', color: '#fff', textAlign: 'center'}}>
            Emitido para / Issued to
          </TableCell>
          <TableCell colSpan={5} sx={{...th, bgcolor: '#0082BB', color: '#fff', textAlign: 'center'}}>
            Usado em / Used as
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={th}>Cpn</TableCell>
          <TableCell sx={th}>Voo / Flight</TableCell>
          <TableCell sx={th}>Rota / Route</TableCell>
          <TableCell sx={th}>Data / Date</TableCell>
          <TableCell sx={th}>Estado / Status</TableCell>
          <TableCell sx={th}>Voo / Flight</TableCell>
          <TableCell sx={th}>Rota / Route</TableCell>
          <TableCell sx={th}>Data / Date</TableCell>
          <TableCell sx={th}>Nº TKT / New TKT</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {coupons.map((c, i) => {
          const status = String(c?.couponStatus ?? '').toUpperCase();
          const flight = `${c?.carrier ?? ''}${c?.flightNumber ?? ''}`;
          const route = `${c?.originAirport ?? ''} → ${c?.destinationAirport ?? ''}`;
          const flownFlight = c?.flownFlightNumber ? `${c?.flownCarrier ?? ''}${c?.flownFlightNumber}` : '—';
          const flownRoute = (c?.flownOriginAirport || c?.flownDestinationAirport)
            ? `${c?.flownOriginAirport ?? ''} → ${c?.flownDestinationAirport ?? ''}`
            : '—';
          return (
            <TableRow key={i} sx={{bgcolor: i % 2 === 0 ? '#FFFFFF' : '#EEF6FB'}}>
              <TableCell sx={{...td, textAlign: 'center', fontWeight: 700}}>{i + 1}</TableCell>
              <TableCell sx={{...td, fontFamily: 'monospace', fontWeight: 700, color: '#002857'}}>{flight}</TableCell>
              <TableCell sx={td}>{route}</TableCell>
              <TableCell sx={td}>{couponDate(c?.date) || '—'}</TableCell>
              <TableCell sx={{...td, textAlign: 'center'}}>
                <Box
                  sx={{
                    display: 'inline-block',
                    textAlign: 'center',
                    borderRadius: '4px',
                    px: '6px',
                    py: '1px',
                    bgcolor: `${COUPON_STATUS_COLORS[status] ?? '#0082BB'}14`,
                  }}
                >
                  <Typography
                    component='span'
                    sx={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      lineHeight: 1.2,
                      display: 'block',
                      whiteSpace: 'nowrap',
                      color: COUPON_STATUS_COLORS[status] ?? '#0082BB',
                    }}
                  >
                    {COUPON_STATUS_LABELS[status]?.pt ?? status ?? '—'}
                  </Typography>
                  {COUPON_STATUS_LABELS[status] && (
                    <Typography
                      component='span'
                      sx={{
                        fontSize: '0.56rem',
                        fontStyle: 'italic',
                        lineHeight: 1.1,
                        display: 'block',
                        whiteSpace: 'nowrap',
                        color: COUPON_STATUS_COLORS[status] ?? '#0082BB',
                        opacity: 0.75,
                      }}
                    >
                      {COUPON_STATUS_LABELS[status].en}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{...td, fontFamily: 'monospace', fontWeight: 700, color: '#002857'}}>{flownFlight}</TableCell>
              <TableCell sx={td}>{flownRoute}</TableCell>
              <TableCell sx={td}>{couponDate(c?.flownDate) || '—'}</TableCell>
              <TableCell sx={td}>{c?.newTicketNumber || '—'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const DeclarationVerifyPage = () => {
  const {token} = useParams<{token?: string}>();
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyFailed, setVerifyFailed] = useState(false);
  const [hashCheck, setHashCheck] = useState<{matches: boolean; reason?: string} | null>(null);
  const [hashChecking, setHashChecking] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const {control, handleSubmit} = useForm<VerifyFormValues>({
    resolver: yupResolver(verifyFormSchema),
    defaultValues: {reference: '', ticketNumber: ''},
  });

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'SATA Verify';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const onSubmitVerification = (data: VerifyFormValues) => {
    setVerifying(true);
    setVerifyFailed(false);
    const verifyUrl = token
      ? `${API_ENDPOINT}/declarations/verify/${token}`
      : `${API_ENDPOINT}/declarations/verify`;
    axios
      .post(verifyUrl, {
        reference: data.reference.trim(),
        ticketNumber: data.ticketNumber.trim(),
      })
      .then((res) => {
        if (res.data?.valid) {
          setResult(res.data);
        } else {
          setVerifyFailed(true);
        }
      })
      .catch(() => setVerifyFailed(true))
      .finally(() => setVerifying(false));
  };

  const details = result?.details ?? {};

  const carrierCode = String(details.carrier ?? details.company ?? '').toUpperCase();
  const isSataAirAcores = carrierCode === 'SP';
  const companyFull = carrierCode
    ? (isSataAirAcores ? 'SATA Air Açores, S.A.' : 'SATA Internacional – Azores Airlines, S.A.')
    : '';
  const companyShort = carrierCode
    ? (isSataAirAcores ? 'SATA Air Açores' : 'Azores Airlines')
    : '';

  const checkFile = (file: File | undefined) => {
    if (!file || !token) return;

    const formData = new FormData();
    formData.append('pdf', file);

    setHashChecking(true);
    setHashCheck(null);
    axios
      .post(`${API_ENDPOINT}/declarations/verify/${token}/check-hash`, formData)
      .then((res) => setHashCheck(res.data))
      .catch(() => setHashCheck({matches: false, reason: 'network_error'}))
      .finally(() => setHashChecking(false));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    checkFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    checkFile(event.dataTransfer.files?.[0]);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #002857 0%, #0082BB 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      {/* Logo / header */}
      <Box sx={{textAlign: 'center', mb: 3}}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 0.75}}>
          <VerifiedUser sx={{color: '#52C6E2', fontSize: '2.2rem'}} />
          <Box>
            <Typography variant='h5' fontWeight='bold' color='white' letterSpacing='-0.01em' sx={{lineHeight: 1.2}}>
              Verificação da Declaração
            </Typography>
            <Typography variant='body2' fontStyle='italic' sx={{color: '#52C6E2', lineHeight: 1.2}}>
              Declaration Verification
            </Typography>
          </Box>
        </Box>
        <Typography variant='body2' sx={{color: 'rgba(255,255,255,0.6)'}}>
          {companyShort || 'Grupo SATA'}
        </Typography>
      </Box>

      <Card sx={{maxWidth: result?.valid ? 760 : 560, width: '100%', borderRadius: 3, boxShadow: '0 24px 64px rgba(0,0,0,0.3)'}}>

        {/* Verification form (2FA-style: reference + ticket number must match the declaration) */}
        {!result?.valid && (
          <CardContent sx={{py: 4, px: 3}}>
            {verifying ? (
              <Box sx={{textAlign: 'center', py: 3}}>
                <CircularProgress sx={{color: '#0082BB'}} />
                <Typography variant='body2' color='text.secondary' sx={{mt: 2}}>
                  A verificar declaração…
                </Typography>
                <Typography variant='caption' fontStyle='italic' sx={{color: '#0082BB', display: 'block'}}>
                  Verifying declaration…
                </Typography>
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmitVerification)}>
                <Typography variant='body2' color='text.secondary' sx={{mb: 0.25}}>
                  Para confirmar a autenticidade, introduza a referência e o número do bilhete indicados na declaração.
                </Typography>
                <Typography variant='body2' fontStyle='italic' sx={{color: '#0082BB', mb: 2.5}}>
                  To confirm authenticity, enter the reference and ticket number shown on the declaration.
                </Typography>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                  <Controller
                    name='reference'
                    control={control}
                    render={({field, fieldState: {error}}) => (
                      <TextField
                        {...field}
                        label='Referência / Reference'
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                  <Controller
                    name='ticketNumber'
                    control={control}
                    render={({field, fieldState: {error}}) => (
                      <TextField
                        {...field}
                        label='Número do Bilhete / Ticket Number'
                        fullWidth
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Box>

                {verifyFailed && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: '#fdecea',
                      border: '1px solid',
                      borderColor: 'error.main',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <Cancel sx={{color: 'error.main', fontSize: '1.4rem', flexShrink: 0}} />
                    <Box>
                      <Typography variant='body2' fontWeight='600' color='error.dark'>
                        Não foi possível verificar esta declaração. Confirme os dados e tente novamente.
                      </Typography>
                      <Typography variant='body2' fontStyle='italic' sx={{color: 'error.dark', opacity: 0.85, mt: 0.25}}>
                        This declaration could not be verified. Please check the details and try again.
                      </Typography>
                    </Box>
                  </Box>
                )}

                <Button type='submit' variant='contained' fullWidth sx={{mt: 2.5, bgcolor: '#0082BB', py: 1.1}}>
                  <Box>
                    <Typography variant='button' fontWeight={700} sx={{display: 'block', lineHeight: 1.3}}>
                      Verificar
                    </Typography>
                    <Typography variant='caption' fontStyle='italic' sx={{display: 'block', lineHeight: 1.2}}>
                      Verify
                    </Typography>
                  </Box>
                </Button>
              </form>
            )}
          </CardContent>
        )}

        {/* Valid */}
        {result?.valid && (
          <CardContent sx={{p: 3}}>
            {/* Valid banner */}
            <Box
              sx={{
                bgcolor: '#e8f5e9',
                border: '1.5px solid',
                borderColor: 'success.main',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2.5,
                py: 2,
                mb: 3,
              }}
            >
              <CheckCircle sx={{color: 'success.main', fontSize: '2.4rem', flexShrink: 0}} />
              <Box>
                <Typography variant='subtitle1' fontWeight='bold' color='success.dark' sx={{lineHeight: 1.3}}>
                  Documento Autêntico
                </Typography>
                <Typography variant='caption' fontStyle='italic' sx={{color: '#0082BB', display: 'block', mb: 0.5}}>
                  Authentic Document
                </Typography>
                <Typography variant='body2' sx={{color: 'success.dark', opacity: 0.8}}>
                  Esta declaração foi emitida por {companyFull || 'Grupo SATA'}, e o conteúdo abaixo
                  corresponde exatamente ao momento da emissão.
                </Typography>
                <Typography variant='body2' fontStyle='italic' sx={{color: '#0082BB', opacity: 0.9, mt: 0.25}}>
                  This declaration was issued by {companyFull || 'Grupo SATA'}, and the content below
                  matches exactly what was issued.
                </Typography>
              </Box>
            </Box>

            {/* Reference + type */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 1,
                mb: 2,
              }}
            >
              <Box>
                <SectionCaption pt='REFERÊNCIA' en='Reference' />
                <Typography
                  variant='h6'
                  fontWeight='bold'
                  sx={{color: '#002857', fontFamily: 'monospace', letterSpacing: '0.05em'}}
                >
                  {result.ref}
                </Typography>
              </Box>
              <Chip
                label={
                  <Box sx={{textAlign: 'center', py: '4px'}}>
                    <Typography variant='caption' fontWeight={700} sx={{display: 'block', lineHeight: 1.3}}>
                      {result.type && TYPE_LABELS[result.type] ? TYPE_LABELS[result.type].pt : result.type}
                    </Typography>
                    {result.type && TYPE_LABELS[result.type] && (
                      <Typography
                        variant='caption'
                        sx={{display: 'block', fontStyle: 'italic', color: '#0082BB', lineHeight: 1.2}}
                      >
                        {TYPE_LABELS[result.type].en}
                      </Typography>
                    )}
                  </Box>
                }
                sx={{
                  bgcolor: '#D6EEF6',
                  color: '#002857',
                  height: 'auto',
                  borderRadius: 1,
                  '& .MuiChip-label': {px: 1.25},
                }}
              />
            </Box>

            <Divider sx={{mb: 2}} />

            {/* Issued at */}
            <Box sx={{mb: 2}}>
              <SectionCaption pt='EMITIDO EM' en='Issued at' />
              <Typography variant='body1'>
                {DateTime.fromISO(result.issuedAt!).toUTC().toFormat("dd MMMM yyyy, HH:mm 'UTC'")}
              </Typography>
            </Box>

            {/* Hash code (verification token) — only present for legacy hash-based links */}
            {token && (
              <Box sx={{mb: 2}}>
                <SectionCaption pt='CÓDIGO HASH' en='Hash code' />
                <Typography variant='body2' sx={{fontFamily: 'monospace', letterSpacing: '0.03em', wordBreak: 'break-all'}}>
                  {token}
                </Typography>
              </Box>
            )}

            {/* Declaration details from metadata */}
            {Object.keys(details).length > 0 && (
              <>
                <Divider sx={{mb: 2}} />
                <Box sx={{mb: 1.25}}>
                  <SectionCaption pt='DETALHES DA DECLARAÇÃO' en='Declaration details' />
                </Box>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                  {/* Flight declarations */}
                  {details.carrier && details.flightNumber && (
                    <DetailRow labelPT='Voo' labelEN='Flight' value={`${details.carrier}${details.flightNumber}`} />
                  )}
                  {details.date && <DetailRow labelPT='Data' labelEN='Date' value={details.date} />}
                  {details.origin && details.destination && (
                    <DetailRow labelPT='Rota' labelEN='Route' value={`${details.origin} → ${details.destination}`} />
                  )}
                  {details.scheduledDeparture && (
                    <DetailRow labelPT='Previsto' labelEN='Scheduled' value={details.scheduledDeparture} />
                  )}
                  {details.actualDeparture && (
                    <DetailRow labelPT='Partida real' labelEN='Actual departure' value={details.actualDeparture} />
                  )}
                  {typeof details.totalDelayMinutes === 'number' && details.totalDelayMinutes > 0 && (
                    <DetailRow labelPT='Atraso' labelEN='Delay' value={`${details.totalDelayMinutes} min`} />
                  )}
                  {Array.isArray(details.passengers) && details.passengers.length > 0 && (
                    <DetailRow
                      labelPT='Passageiros'
                      labelEN='Passengers'
                      value={details.passengers.map((p: any) => p.name).filter(Boolean).join(', ')}
                    />
                  )}
                  {Array.isArray(details.reasons) && details.reasons.length > 0 && (
                    <DetailRow
                      labelPT='Motivo'
                      labelEN='Reason'
                      value={details.reasons.map((r: any) => r.textEN || r.textPT).filter(Boolean).join('; ')}
                    />
                  )}
                  {/* Ticket usage */}
                  {(details.passengerFirstName || details.passengerLastName) && (
                    <DetailRow
                      labelPT='Passageiro'
                      labelEN='Passenger'
                      value={`${details.passengerFirstName ?? ''} ${details.passengerLastName ?? ''}`.trim()}
                    />
                  )}
                  {details.ticketNumber && <DetailRow labelPT='Bilhete' labelEN='Ticket' value={details.ticketNumber} />}
                  {details.reservationCode && (
                    <DetailRow labelPT='Reserva' labelEN='Reservation' value={details.reservationCode} />
                  )}
                </Box>
                {Array.isArray(details.coupons) && details.coupons.length > 0 && (
                  <Box sx={{mt: 1.5}}>
                    <CouponsTable coupons={details.coupons} />
                  </Box>
                )}
              </>
            )}

            {/* Upload-to-verify PDF integrity check */}
            {SHOW_FILE_INTEGRITY_CHECK && (
              <>
                <Divider sx={{mb: 2}} />
                <Box sx={{mb: 1.25}}>
                  <SectionCaption pt='VERIFICAR INTEGRIDADE DO FICHEIRO' en='Verify file integrity' />
                </Box>
                <Typography variant='body2' color='text.secondary' sx={{mb: 0.25}}>
                  Carregue o PDF que recebeu para confirmar que corresponde exatamente ao que foi emitido.
                </Typography>
                <Typography variant='body2' fontStyle='italic' sx={{color: '#0082BB', mb: 1.5}}>
                  Upload the PDF you received to confirm it matches exactly what was issued.
                </Typography>
                <Box
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    p: 2.5,
                    borderRadius: 2,
                    border: '2px dashed',
                    borderColor: isDragging ? '#0082BB' : '#D6EEF6',
                    bgcolor: isDragging ? '#EEF6FB' : 'transparent',
                    transition: 'background-color 0.15s, border-color 0.15s',
                  }}
                >
                  <CloudUpload sx={{color: '#0082BB', fontSize: '2rem'}} />
                  <Box sx={{textAlign: 'center'}}>
                    <Typography variant='body2' color='text.secondary'>
                      Arraste o PDF para aqui, ou
                    </Typography>
                    <Typography variant='caption' fontStyle='italic' sx={{color: '#0082BB', display: 'block'}}>
                      Drag and drop the PDF here, or
                    </Typography>
                  </Box>
                  <Button
                    component='label'
                    variant='outlined'
                    disabled={hashChecking}
                    sx={{borderColor: '#0082BB', color: '#0082BB', textAlign: 'center', lineHeight: 1.3, py: 0.75}}
                  >
                    <Box>
                      <Typography variant='button' fontWeight={700} sx={{display: 'block', lineHeight: 1.3}}>
                        {hashChecking ? 'A verificar…' : 'Escolher ficheiro'}
                      </Typography>
                      <Typography variant='caption' fontStyle='italic' sx={{display: 'block', lineHeight: 1.2}}>
                        {hashChecking ? 'Checking…' : 'Choose file'}
                      </Typography>
                    </Box>
                    <input type='file' accept='application/pdf' hidden onChange={handleFileUpload} />
                  </Button>
                </Box>
                {hashCheck && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: hashCheck.matches ? '#e8f5e9' : '#fdecea',
                      border: '1px solid',
                      borderColor: hashCheck.matches ? 'success.main' : 'error.main',
                    }}
                  >
                    <Typography
                      variant='body2'
                      fontWeight='600'
                      color={hashCheck.matches ? 'success.dark' : 'error.dark'}
                    >
                      {hashCheck.matches
                        ? 'Este ficheiro corresponde exatamente ao documento original.'
                        : hashCheck.reason === 'no_hash_on_record'
                          ? 'Esta declaração foi emitida antes da verificação de integridade estar disponível, pelo que não pode ser verificada desta forma.'
                          : 'Este ficheiro não corresponde ao documento original.'}
                    </Typography>
                    <Typography
                      variant='body2'
                      fontStyle='italic'
                      sx={{color: hashCheck.matches ? 'success.dark' : 'error.dark', opacity: 0.85, mt: 0.25}}
                    >
                      {hashCheck.matches
                        ? 'This file matches the original document exactly.'
                        : hashCheck.reason === 'no_hash_on_record'
                          ? 'This declaration was issued before integrity checking was available, so it cannot be verified this way.'
                          : 'This file does not match the original document.'}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Footer stripe */}
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: '3px solid #52C6E2',
                textAlign: 'center',
              }}
            >
              <Typography variant='caption' color='text.secondary'>
                Emitido por / Issued by {companyFull || 'Grupo SATA'}
              </Typography>
            </Box>

            {/* Legal disclaimer — electronic issuance / eIDAS note */}
            <Box sx={{mt: 2, pt: 1.5}}>
              <Typography
                variant='caption'
                component='p'
                sx={{color: '#3D3D3D', lineHeight: 1.5, textAlign: 'justify', display: 'block'}}
              >
                Este documento foi emitido eletronicamente pelo {companyFull || 'Grupo SATA'} e
                dispensa assinatura manuscrita, nos termos do Decreto-Lei n.º 12/2021, de 9 de fevereiro, que
                executa na ordem jurídica portuguesa o Regulamento (UE) n.º 910/2014 (eIDAS). Este mecanismo
                constitui uma verificação eletrónica de referência e não uma assinatura ou selo eletrónico
                qualificado.
              </Typography>
              <Typography
                variant='caption'
                component='p'
                sx={{
                  color: '#0082BB',
                  fontStyle: 'italic',
                  lineHeight: 1.5,
                  textAlign: 'justify',
                  display: 'block',
                  mt: 0.75,
                }}
              >
                This document was issued electronically by {companyFull || 'Grupo SATA'} and
                does not require a handwritten signature, pursuant to Decree-Law No. 12/2021, of 9 February,
                implementing Regulation (EU) No 910/2014 (eIDAS). This mechanism constitutes a reference-based
                electronic verification, not a qualified electronic signature or seal.
              </Typography>
            </Box>
          </CardContent>
        )}
      </Card>

      {/* Token shown at bottom for reference — only present for legacy hash-based links */}
      {token && (
        <Typography variant='caption' sx={{color: 'rgba(255,255,255,0.25)', mt: 3, fontFamily: 'monospace'}}>
          {token}
        </Typography>
      )}
    </Box>
  );
};

export default DeclarationVerifyPage;
